/**
 * Permission Viewer Tab Component
 * Read-only view for analyzing grant permissions across resources
 *
 * Features:
 * - View mode selector (By Subject/By Grant)
 * - Target filtering (Domain, Resource Type)
 * - Subject selection (Role/User)
 * - Grant selector (in By Grant mode) or Resource search (in By Subject mode)
 * - Read-only permission grid with inheritance indicators
 * - Visual legend for permission states
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { API } from 'aws-amplify';
import { listClients, listUsers, listResourceTypes } from '../../../../../graphql/queries';
import { useUser } from '../../../../../hooks/useUser';
import { useGrantGrid } from '../hooks/useGrantGrid';
import { ACTIONS, CELL_STATES } from '../services/grantGridService';

/**
 * View modes for the permission viewer
 */
const VIEW_MODES = [
  { value: 'BY_SUBJECT', label: 'By Subject (Role/User)' },
  { value: 'BY_GRANT', label: 'By Grant' }
];

/**
 * Domain options for filtering
 */
const DOMAINS = [
  { value: 'ALL', label: 'All Domains' },
  { value: 'OPERATIONS', label: 'Operations' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'HR', label: 'Human Resources' },
  { value: 'MARKETING', label: 'Marketing' }
];

/**
 * Resource type options for filtering
 */
const RESOURCE_TYPES = [
  { value: 'ALL', label: 'All Types' },
  { value: 'REPORT', label: 'Report' },
  { value: 'DASHBOARD', label: 'Dashboard' },
  { value: 'PAGE', label: 'Page' },
  { value: 'ADMIN', label: 'Admin' }
];

/**
 * Group Role options for subject selection
 */
const GROUP_ROLES = [
  { value: 'MANAGER', label: 'MANAGER' },
  { value: 'ASSOCIATE', label: 'ASSOCIATE' },
  { value: 'CLIENT_ADMIN', label: 'CLIENT_ADMIN' },
  { value: 'APP_ADMIN', label: 'APP_ADMIN' }
];

/**
 * Permission cell component - displays permission state with inheritance badges
 * Top section: Own permission icon
 * Bottom section: Inheritance indicators (C/P/Pa) as colored circles
 */
function PermissionCell({ cellState, action, resource }) {
  const { state, grants } = cellState;

  // Check if this action is available for this resource
  const isActionAvailable = resource?.defaultActions?.includes(action) ?? true;

  // Extract inheritance flags from the first grant's scopeConfig
  // (if multiple grants exist, show badges from ALLOW grant, or first grant)
  const activeGrant = grants.find(g => g.effect === 'ALLOW') || grants[0];
  const scopeConfig = activeGrant?.scopeConfig;
  const grantEffect = activeGrant?.effect;

  const hasChildren = scopeConfig?.includeChildren || false;
  const hasPeers = scopeConfig?.includePeers || false;
  const hasParent = scopeConfig?.includeParent || false;

  // Determine icon based on state
  const renderIcon = () => {
    // If action is not available for this resource, show black filled circle
    if (!isActionAvailable) {
      return (
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: '#000000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
      );
    }

    switch (state) {
      case CELL_STATES.ALLOW:
        return <CheckIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
      case CELL_STATES.DENY:
        return <CloseIcon sx={{ color: '#f44336', fontSize: 20 }} />;
      case CELL_STATES.CONFLICT:
        return <CheckIcon sx={{ color: '#000000', fontSize: 20 }} />;
      case CELL_STATES.NONE:
      default:
        return <RemoveIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
    }
  };

  // Render inheritance badge circle with letter
  const renderInheritanceBadge = (label, isEnabled) => {
    if (!isEnabled) return null;

    // Color based on grant effect: green for ALLOW, red for DENY
    const bgColor = grantEffect === 'ALLOW' ? '#4caf50' : '#f44336';

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          height: 18,
          width: 18,
          fontSize: '0.65rem',
          fontWeight: 'bold',
          bgcolor: bgColor,
          color: 'white',
          borderRadius: '50%',
          '& .MuiChip-label': { px: 0, py: 0 }
        }}
      />
    );
  };

  // Render inheritance badges on bottom section
  const renderInheritanceIndicators = () => {
    // Don't show inheritance for unavailable actions
    if (!isActionAvailable) {
      return null;
    }

    if (state === CELL_STATES.NONE || grants.length === 0) {
      return null;
    }

    const badges = [];
    if (hasChildren) badges.push(renderInheritanceBadge('C', true));
    if (hasPeers) badges.push(renderInheritanceBadge('P', true));
    if (hasParent) badges.push(renderInheritanceBadge('Pa', true));

    if (badges.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center', mt: 0.25 }}>
        {badges}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
      {/* Top: Own Permission */}
      <Box>{renderIcon()}</Box>
      {/* Bottom: Inheritance (C/P/Pa) as colored circles */}
      {renderInheritanceIndicators()}
    </Box>
  );
}

/**
 * Permission Viewer Tab Component
 */
function PermissionViewerTab({ clientId, grants = [] }) {
  // Get user to determine if APP_ADMIN or CLIENT_ADMIN
  const { user } = useUser();
  const isAppAdmin = user?.groupRole === 'APP_ADMIN';

  // View configuration state
  const [viewMode, setViewMode] = useState('BY_SUBJECT');
  const [selectedDomain, setSelectedDomain] = useState('ALL');
  const [selectedResourceType, setSelectedResourceType] = useState('ALL');
  const [searchText, setSearchText] = useState('');
  const [subjectType, setSubjectType] = useState('ROLE');
  const [selectedRole, setSelectedRole] = useState(user?.groupRole || 'MANAGER');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedGrant, setSelectedGrant] = useState('');

  // Client selection state
  const [selectedClient, setSelectedClient] = useState(clientId || '');
  const [availableClients, setAvailableClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Users list state
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Resource types map state
  const [resourceTypesMap, setResourceTypesMap] = useState(new Map());

  // Ref to track previous view mode for detecting mode switches
  const prevViewModeRef = React.useRef(viewMode);

  // Ref to track previous subject type for detecting subject type switches
  const prevSubjectTypeRef = React.useRef(subjectType);

  // Ref to track if initial role has been set (to prevent overriding user selections)
  const initialRoleSetRef = React.useRef(false);

  // Use grant grid hook for data (use selectedClient instead of clientId prop)
  const {
    resources,
    gridState,
    subjectCriteria,
    isLoading,
    error,
    updateSubjectCriteria,
    getCellState
  } = useGrantGrid(selectedClient);

  // Debug: Log when resources or gridState changes
  React.useEffect(() => {
    if (resources.length > 0 || gridState.size > 0) {
      console.log('=== PERMISSION VIEWER DEBUG ===');
      console.log('Selected Client:', selectedClient);
      console.log('Subject Criteria:', subjectCriteria);
      console.log('Resources loaded:', resources.length);
      console.log('Resources:', resources);
      console.log('Grid State size:', gridState.size);
      console.log('Grid State entries:');
      gridState.forEach((resourceRow, resourceId) => {
        const resourceInfo = resources.find(r => r.resourceId === resourceId);
        console.log(`  Resource: ${resourceInfo?.catalogName || resourceId} (${resourceId})`);
        resourceRow.forEach((cell, action) => {
          if (cell.state !== 'NONE') {
            console.log(`    ${action}: ${cell.state}`, {
              grantsCount: cell.grants.length,
              grants: cell.grants.map(g => ({
                id: g.id,
                name: g.name,
                effect: g.effect,
                targetResourceId: g.targetResourceId,
                targetDomain: g.targetDomain,
                targetResourceTypeId: g.targetResourceTypeId
              }))
            });
          }
        });
      });
      console.log('=== END DEBUG ===');
    }
  }, [resources, gridState, selectedClient, subjectCriteria]);

  // Fetch available clients for APP_ADMIN users
  React.useEffect(() => {
    const fetchClients = async () => {
      if (isAppAdmin) {
        setClientsLoading(true);
        try {
          const response = await API.graphql({
            query: listClients,
            variables: { limit: 1000 }
          });
          const clientsList = response.data.listClients.items || [];
          // Sort clients alphabetically by clientName
          const sortedClients = clientsList.sort((a, b) => {
            const nameA = (a.clientName || a.clientId).toLowerCase();
            const nameB = (b.clientName || b.clientId).toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setAvailableClients(sortedClients);
          console.log('Loaded clients for APP_ADMIN:', sortedClients);
        } catch (error) {
          console.error('Error fetching clients:', error);
        } finally {
          setClientsLoading(false);
        }
      }
    };

    fetchClients();
  }, [isAppAdmin]);

  // Fetch resource types to create a lookup map
  React.useEffect(() => {
    const fetchResourceTypes = async () => {
      try {
        const response = await API.graphql({
          query: listResourceTypes,
          variables: { limit: 1000 }
        });
        const resourceTypesList = response.data.listResourceTypes.items || [];
        // Create a map from id to name
        const typesMap = new Map(
          resourceTypesList.map(rt => [rt.id, rt.name])
        );
        setResourceTypesMap(typesMap);
        console.log('Loaded resource types:', resourceTypesList.length);
      } catch (error) {
        console.error('Error fetching resource types:', error);
      }
    };

    fetchResourceTypes();
  }, []);

  // Set initial client selection based on user role
  React.useEffect(() => {
    if (clientId && !selectedClient) {
      // Both APP_ADMIN and CLIENT_ADMIN: default to user's client
      setSelectedClient(clientId);
    }
  }, [clientId, selectedClient]);

  // Set initial role flag once user loads (prevents auto-override of manual selections)
  React.useEffect(() => {
    if (user?.groupRole && !initialRoleSetRef.current) {
      console.log('Initial user loaded, setting flag. groupRole:', user.groupRole);
      initialRoleSetRef.current = true;
      // Only update selectedRole if it's still the default MANAGER and user is not MANAGER
      if (selectedRole === 'MANAGER' && user.groupRole !== 'MANAGER') {
        console.log('Updating initial selectedRole to:', user.groupRole);
        setSelectedRole(user.groupRole);
      }
    }
  }, [user]); // Only depend on user, not selectedRole

  // Fetch users when client changes and subject type is USER
  React.useEffect(() => {
    const fetchUsers = async () => {
      if (selectedClient && subjectType === 'USER') {
        setUsersLoading(true);
        try {
          const response = await API.graphql({
            query: listUsers,
            variables: {
              filter: { client: { eq: selectedClient } },
              limit: 1000
            }
          });
          const usersList = response.data.listUsers.items || [];
          // Sort users alphabetically by userName or email
          const sortedUsers = usersList.sort((a, b) => {
            const nameA = (a.userName || a.email || a.id).toLowerCase();
            const nameB = (b.userName || b.email || b.id).toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setAvailableUsers(sortedUsers);
          console.log('Loaded users for client:', selectedClient, sortedUsers);
        } catch (error) {
          console.error('Error fetching users:', error);
          setAvailableUsers([]);
        } finally {
          setUsersLoading(false);
        }
      }
    };

    fetchUsers();
  }, [selectedClient, subjectType]);

  // Reset subject value when subjectType changes
  React.useEffect(() => {
    // Only reset when subjectType actually changes, not when user object updates
    if (prevSubjectTypeRef.current !== subjectType) {
      if (subjectType === 'ROLE') {
        // Reset to user's groupRole when switching to ROLE
        setSelectedRole(user?.groupRole || 'MANAGER');
        setSelectedUser(''); // Clear user selection
      } else if (subjectType === 'USER') {
        // Reset to empty when switching to USER (user must select)
        setSelectedUser('');
      }

      // Update the ref for next render
      prevSubjectTypeRef.current = subjectType;
    }
  }, [subjectType, user]);

  // Set subject criteria based on type and selected value
  React.useEffect(() => {
    const subjectValue = subjectType === 'ROLE' ? selectedRole : selectedUser;
    console.log('Setting subject criteria to:', { subjectType, subjectValue });
    updateSubjectCriteria({
      subjectType,
      subjectValue
    });
  }, [subjectType, selectedRole, selectedUser, updateSubjectCriteria]);

  // Debug: log grants array
  React.useEffect(() => {
    console.log('PermissionViewerTab grants:', grants);
  }, [grants]);

  // Populate filters when a grant is selected in BY_GRANT mode
  React.useEffect(() => {
    if (viewMode === 'BY_GRANT' && selectedGrant) {
      // Find the grant object from the grants array
      const grant = grants.find(g => g.id === selectedGrant);

      if (grant) {
        console.log('Populating filters from selected grant:', grant);

        // Populate target filters - handle wildcards (null, undefined, or "*") as "ALL"
        if (grant.targetDomain && grant.targetDomain !== '*') {
          setSelectedDomain(grant.targetDomain);
        } else {
          setSelectedDomain('ALL'); // Wildcard should show "All Domains"
        }

        if (grant.targetResourceTypeId && grant.targetResourceTypeId !== '*') {
          setSelectedResourceType(grant.targetResourceTypeId);
        } else {
          setSelectedResourceType('ALL'); // Wildcard should show "All Types"
        }

        // Populate subject selection
        if (grant.subjectType && grant.subjectValue) {
          setSubjectType(grant.subjectType);
          if (grant.subjectType === 'ROLE') {
            setSelectedRole(grant.subjectValue);
          } else if (grant.subjectType === 'USER') {
            setSelectedUser(grant.subjectValue);
          }
          // Update the grid's subject criteria
          updateSubjectCriteria({
            subjectType: grant.subjectType,
            subjectValue: grant.subjectValue
          });
        }
      }
    }
  }, [viewMode, selectedGrant, grants, updateSubjectCriteria]);

  // Clear filters when switching from BY_GRANT to BY_SUBJECT mode
  React.useEffect(() => {
    // If switching from BY_GRANT to BY_SUBJECT
    if (prevViewModeRef.current === 'BY_GRANT' && viewMode === 'BY_SUBJECT') {
      console.log('Switching from BY_GRANT to BY_SUBJECT - clearing filters');

      const defaultRole = user?.groupRole || 'MANAGER';

      // Clear target filters
      setSelectedDomain('ALL');
      setSelectedResourceType('ALL');

      // Reset subject selection to default
      setSubjectType('ROLE');
      setSelectedRole(defaultRole);
      setSelectedUser(''); // Clear user selection

      // Clear grant selection
      setSelectedGrant('');

      // Update grid's subject criteria to default
      updateSubjectCriteria({
        subjectType: 'ROLE',
        subjectValue: defaultRole
      });
    }

    // Update the ref for next render
    prevViewModeRef.current = viewMode;
  }, [viewMode, updateSubjectCriteria, user]);

  // Filter resources based on search and filters
  const filteredResources = useMemo(() => {
    let filtered = resources;

    // Filter by domain
    if (selectedDomain !== 'ALL') {
      filtered = filtered.filter(r => r.domain === selectedDomain);
    }

    // Filter by resource type
    if (selectedResourceType !== 'ALL') {
      filtered = filtered.filter(r => r.resourceTypeId === selectedResourceType);
    }

    // Filter by search text
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(r =>
        (r.customLabel?.toLowerCase().includes(search)) ||
        (r.resourceId?.toLowerCase().includes(search)) ||
        (r.catalogName?.toLowerCase().includes(search))
      );
    }

    return filtered;
  }, [resources, selectedDomain, selectedResourceType, searchText]);

  if (!selectedClient) {
    return (
      <Alert severity="warning">
        No client selected. Please ensure you are logged in or select a client.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Filters Section */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight="bold" display="block" sx={{ mb: 2 }}>
          Filters
        </Typography>

        {/* Top Row - Client, View Mode, Search */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          {/* Client Selector */}
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
              Client
            </Typography>
            {isAppAdmin ? (
              <FormControl fullWidth size="small">
                <Select
                  value={selectedClient || ''}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  disabled={clientsLoading}
                  displayEmpty
                >
                  {clientsLoading ? (
                    <MenuItem value="">
                      <em>Loading clients...</em>
                    </MenuItem>
                  ) : availableClients.length === 0 ? (
                    <MenuItem value="">
                      <em>No clients available</em>
                    </MenuItem>
                  ) : (
                    availableClients.map((client) => (
                      <MenuItem key={client.id} value={client.clientId}>
                        {client.clientName || client.clientId}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            ) : (
              <TextField
                value={clientId || ''}
                size="small"
                fullWidth
                disabled
                InputProps={{
                  sx: { bgcolor: '#f5f5f5' }
                }}
              />
            )}
          </Box>

          {/* View Mode Selector */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
              View Mode
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
              >
                {VIEW_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Conditionally render Grant Selector or Search Resources based on view mode */}
          {viewMode === 'BY_GRANT' ? (
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                Select Grant
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedGrant}
                  onChange={(e) => setSelectedGrant(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select a grant...</em>
                  </MenuItem>
                  {grants.map((grant) => (
                    <MenuItem key={grant.id} value={grant.id}>
                      {grant.name || grant.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
                Search Resources
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Filter resources..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>
          )}
        </Box>

        {/* Filter Boxes Row */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Target Filters Panel */}
          <Paper sx={{ flex: 1, p: 2 }}>
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 2 }}>
              Target Filters
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Domain Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Domain</InputLabel>
                <Select
                  value={selectedDomain}
                  onChange={(e) => setSelectedDomain(e.target.value)}
                  label="Domain"
                >
                  {DOMAINS.map((domain) => (
                    <MenuItem key={domain.value} value={domain.value}>
                      {domain.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Resource Type Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Resource Type</InputLabel>
                <Select
                  value={selectedResourceType}
                  onChange={(e) => setSelectedResourceType(e.target.value)}
                  label="Resource Type"
                >
                  {RESOURCE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Subject Selection Panel */}
          <Paper sx={{ flex: 1, p: 2 }}>
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 2 }}>
              Subject Selection
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Subject Type */}
              <FormControl fullWidth size="small">
                <InputLabel>Subject Type</InputLabel>
                <Select
                  value={subjectType}
                  onChange={(e) => setSubjectType(e.target.value)}
                  label="Subject Type"
                >
                  <MenuItem value="ROLE">Role</MenuItem>
                  <MenuItem value="USER">User</MenuItem>
                </Select>
              </FormControl>

              {/* Subject Value */}
              {subjectType === 'ROLE' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Subject Value</InputLabel>
                  <Select
                    value={selectedRole || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log('ROLE onChange fired:', newValue);
                      setSelectedRole(newValue);
                    }}
                    label="Subject Value"
                  >
                    {GROUP_ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>Subject Value</InputLabel>
                  { console.log('RENDER SubjectType:', subjectType, 'selectedUser:', selectedUser) }
                  <Select
                    value={selectedUser || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      console.log('USER onChange fired:', newValue);
                      setSelectedUser(newValue);
                    }}
                    onOpen={() => console.log('USER Select opened')}
                    onClose={() => console.log('USER Select closed')}
                    label="Subject Value"
                    disabled={usersLoading}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{usersLoading ? 'Loading users...' : 'Select a user...'}</em>
                    </MenuItem>
                    {!usersLoading && availableUsers.map((userItem) => (
                      <MenuItem
                        key={userItem.id}
                        value={userItem.id}
                      >
                        {userItem.userName || userItem.email || userItem.id}
                        {userItem.firstName && userItem.lastName && ` (${userItem.firstName} ${userItem.lastName})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Paper>
        </Box>
      </Paper>

      {/* Permissions Section */}
      <Paper sx={{ flex: 1 }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Typography variant="h6" fontWeight="bold" display="block" sx={{ mb: 2 }}>
            Permissions
          </Typography>
        </Box>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box p={4}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : filteredResources.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="body1" color="text.secondary">
              No resources found matching the current filters
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: '#fff'
            }}>
              <thead>
                <tr>
                  <th style={{
                    position: 'sticky',
                    top: 0,
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    backgroundColor: '#f5f5f5',
                    minWidth: 120,
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    zIndex: 2
                  }}>
                    Domain
                  </th>
                  <th style={{
                    position: 'sticky',
                    top: 0,
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    backgroundColor: '#f5f5f5',
                    minWidth: 120,
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    zIndex: 2
                  }}>
                    Resource Type
                  </th>
                  <th style={{
                    position: 'sticky',
                    top: 0,
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    backgroundColor: '#f5f5f5',
                    minWidth: 200,
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    zIndex: 2
                  }}>
                    Resource
                  </th>
                  {ACTIONS.map((action) => (
                    <th
                      key={action}
                      style={{
                        position: 'sticky',
                        top: 0,
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        backgroundColor: '#f5f5f5',
                        minWidth: 100,
                        padding: '12px 16px',
                        textAlign: 'center',
                        borderBottom: '2px solid #e0e0e0',
                        zIndex: 2
                      }}
                    >
                      {action}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredResources.map((resource, index) => (
                  <tr
                    key={resource.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa'
                    }}
                  >
                    <td style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <Typography variant="body2">
                        {resource.domain || '-'}
                      </Typography>
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <Typography variant="body2">
                        {resource.resourceTypeId && resourceTypesMap.has(resource.resourceTypeId)
                          ? resourceTypesMap.get(resource.resourceTypeId)
                          : resource.resourceTypeId || '-'}
                      </Typography>
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <Typography variant="body2" fontWeight="bold">
                        {resource.customLabel || resource.catalogName || resource.resourceId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {resource.resourceCode || resource.resourceId}
                      </Typography>
                    </td>
                    {ACTIONS.map((action) => {
                      const cellState = getCellState(resource.resourceId, action);

                      return (
                        <td
                          key={action}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            borderBottom: '1px solid #e0e0e0'
                          }}
                        >
                          <PermissionCell
                            cellState={cellState}
                            action={action}
                            resource={resource}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Permission States */}
          <Box>
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
              Own Permission States (Top):
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                <Typography variant="caption">Allow</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CloseIcon sx={{ color: '#f44336', fontSize: 16 }} />
                <Typography variant="caption">Explicit Deny</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RemoveIcon sx={{ color: '#9e9e9e', fontSize: 16 }} />
                <Typography variant="caption">Implicit Deny</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: '#000000'
                  }}
                />
                <Typography variant="caption">Not Available</Typography>
              </Box>
            </Box>
          </Box>

          {/* Inheritance Indicators */}
          <Box>
            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 1 }}>
              Inheritance Indicators (Bottom):
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip label="C" size="small" sx={{ height: 18, width: 18, fontSize: '0.65rem', bgcolor: '#4caf50', color: 'white', borderRadius: '50%', '& .MuiChip-label': { px: 0, py: 0 } }} />
                <Typography variant="caption">Children</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip label="P" size="small" sx={{ height: 18, width: 18, fontSize: '0.65rem', bgcolor: '#4caf50', color: 'white', borderRadius: '50%', '& .MuiChip-label': { px: 0, py: 0 } }} />
                <Typography variant="caption">Peers</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip label="Pa" size="small" sx={{ height: 18, width: 18, fontSize: '0.65rem', bgcolor: '#4caf50', color: 'white', borderRadius: '50%', '& .MuiChip-label': { px: 0, py: 0 } }} />
                <Typography variant="caption">Parent</Typography>
              </Box>
              <Box sx={{ ml: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  (Green = ALLOW, Red = DENY)
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default PermissionViewerTab;
